/**
 * Speech Relay Auth
 *
 * Provides the connection details the Azure Speech SDK needs to reach the
 * backend WebSocket relay (server-speech-proxy Phase 2) instead of talking to
 * Azure directly.
 *
 * The relay is a dumb authenticated pass-through: the SDK connects with
 * `SpeechConfig.fromHost(<api-host>)` and sets its `authorizationToken` to the
 * Supabase session JWT. The SDK forwards that JWT as an
 * `Authorization: Bearer <token>` header on the WebSocket upgrade, which the
 * relay validates before opening the upstream Azure socket with the real
 * subscription key injected server-side. Credentials never reach the device.
 *
 * This is the sole client-side speech auth path (STT + pronunciation
 * assessment). Client-direct Azure speech-token minting was retired — TTS now
 * synthesizes server-side via POST /api/speech/tts (no device credential).
 *
 * See: docs/specs/api/server-speech-proxy.api.md §Phase 2,
 *      docs/specs/mobile/speech-relay.mobile.md
 */

import { getApiUrl } from '../config/constants';
import { getAuthProvider } from '../auth';

export interface SpeechRelayConnection {
  /**
   * WebSocket origin of the backend relay (scheme + host, NO path). The Azure
   * Speech SDK's `fromHost()` forbids a path — it auto-appends
   * `/speech/recognition/conversation/cognitiveservices/v1?<params>`, which the
   * relay claims under `/speech/*`. Example: `wss://api-weu-stg.fluencia.io`
   * or `ws://192.168.1.10:3000` in dev.
   */
  host: string;
  /** Supabase session JWT — sent by the SDK as `Authorization: Bearer <token>`. */
  token: string;
}

/**
 * Derive the relay's WebSocket origin from the HTTP API URL.
 *
 * Mirrors `getSocketUrl()` in services/socket.ts: strip the trailing `/api`
 * path, then swap the HTTP scheme for its WebSocket equivalent
 * (`https`→`wss`, `http`→`ws`). No path is appended — `fromHost()` requires a
 * bare origin.
 */
export function getSpeechRelayHost(): string {
  const apiUrl = getApiUrl();
  // Strip the /api path suffix to get the server origin (same as getSocketUrl).
  const origin = apiUrl.replace(/\/api\/?$/, '');
  // Swap http(s) → ws(s). `https` must be checked before `http` (prefix).
  return origin.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
}

/**
 * Get the host + token needed to open a recognizer session through the relay.
 *
 * The Supabase session is short-lived; the recognizer session that consumes
 * this connection is likewise short (a single take), so no in-session refresh
 * is needed — the same trade-off the socket auth manager makes.
 *
 * @throws Error('no_auth_token') when there is no active session.
 */
export async function getSpeechRelayConnection(): Promise<SpeechRelayConnection> {
  const token = await getAuthProvider().getAccessToken();
  if (!token) {
    const err = new Error('No auth token available for speech relay');
    (err as Error & { code: string }).code = 'no_auth_token';
    throw err;
  }
  return { host: getSpeechRelayHost(), token };
}
